import { CSSProperties, ReactNode } from 'react';

/**
 * TripContextBar — the strip under a chat thread header carrying trip
 * context: status dot + label, hairline-separated date range, an optional
 * highlighted "Day X of Y" segment, and a right-aligned action slot
 * (typically an outline Button — "Open handbook").
 */
export interface TripContextBarProps {
  /** Status label, e.g. "Building itinerary". */
  statusLabel: string;
  /** CSS colour for the status dot. Default --text-dim. */
  statusColor?: string;
  /** Date range, e.g. "Jul 5–8". */
  dates?: string;
  /** Live progress segment, e.g. "Day 1 of 4" (rendered in success green). */
  dayOf?: string;
  /** Right-aligned action (e.g. <Button variant="outline">Open handbook</Button>). */
  action?: ReactNode;
  style?: CSSProperties;
}

const SEP = <span style={{ width: 1, height: 13, background: 'var(--line)', flexShrink: 0 }} />;

export function TripContextBar({ statusLabel, statusColor, dates, dayOf, action, style }: TripContextBarProps) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        padding: '11px 24px', background: 'var(--surface)', borderBottom: '1px solid var(--line)',
        fontFamily: 'var(--font-sans)', flexShrink: 0,
        ...style,
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-soft)' }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusColor ?? 'var(--text-dim)', flexShrink: 0 }} />
        {statusLabel}
      </span>
      {dates ? (
        <>
          {SEP}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-dim)' }}>
            <i className="ti ti-calendar-event" style={{ fontSize: 13 }} />{dates}
          </span>
        </>
      ) : null}
      {dayOf ? (
        <>
          {SEP}
          <span style={{ fontSize: 11.5, color: 'var(--success-fg)', fontWeight: 500 }}>{dayOf}</span>
        </>
      ) : null}
      <div style={{ flex: 1, minWidth: 12 }} />
      {action}
    </div>
  );
}

export default TripContextBar;
